import React, { useState, useEffect, useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Cpu, CheckCircle2, ChevronDown, Lock, Layers, Download, ChevronRight } from "lucide-react";
import Footer from "../components/Footer";
import Breadcrumb from "../components/Breadcrumb";
import { ScrollReveal } from "../components/story/ScrollReveal";
import { TypewriterHeadline } from "../components/story/TypewriterHeadline";
import ArchitectureStack from "../components/story/ArchitectureStack";
import { InfiniteMarquee } from "../components/story/InfiniteMarquee";
import { BenchmarkBarMatrix } from "../components/story";
import { useAnimationCoordinator } from "../hooks/useAnimationCoordinator";

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "name": "Dopove",
      "url": "https://dopove.com",
      "logo": "https://dopove.com/logo_new_16x9_trans_inverted.png",
      "sameAs": [
        "https://x.com/dopove",
        "https://peerlist.io/dopove",
        "https://github.com/dopove",
        "https://linkedin.com/company/dopove"
      ],
      "foundingDate": "2024",
      "description": "Dopove builds useful memory for AI agents and applies systems thinking to how it works. Its first product is ICE, the Indexless Context Engine."
    },
    {
      "@type": "SoftwareApplication",
      "name": "GoingMerry",
      "applicationCategory": "DeveloperApplication",
      "operatingSystem": "Cross-platform",
      "offers": {
        "@type": "OfferCatalog",
        "name": "GoingMerry Licensing Tiers",
        "itemListElement": [
          {
            "@type": "Offer",
            "name": "Community Edition",
            "description": "Free local execution runtime",
            "price": "0",
            "priceCurrency": "USD",
            "availability": "https://schema.org/PreOrder",
            "url": "https://dopove.com/download",
            "eligibleRegion": {
              "@type": "GeoShape",
              "addressCountry": "WORLD"
            }
          },
          {
            "@type": "Offer",
            "name": "Enterprise Edition",
            "description": "Deploy, secure, and monitor local models at scale with fleet management",
            "priceCurrency": "USD",
            "availability": "https://schema.org/PreOrder",
            "url": "mailto:info@dopove.com",
            "priceSpecification": {
              "@type": "UnitPriceSpecification",
              "priceCurrency": "USD",
              "minPrice": "0"
            },
            "eligibleRegion": {
              "@type": "GeoShape",
              "addressCountry": "WORLD"
            }
          }
        ]
      },
      "url": "https://dopove.com/goingmerry",
      "description": "GoingMerry is a local AI runtime for running models privately on your own hardware. It is the next product we are building after ICE.",
      "featureList": [
        "Native kernels - custom CUDA, Metal and AVX kernels",
        "Local by default - air-gapped and on-prem deployment",
        "Several hardware targets - NVIDIA GPUs, Apple Silicon, x86 CPUs",
        "Custom C/C++ Compiler Toolchain - AVX-512/NEON vectorization",
        "Cache-Aware Runtime - L3 cache tunnels for memory latency",
        "Go + C++ Hybrid Engine - High-level orchestration, low-level tensor engine"
      ],
      "applicationSubCategory": "AI/ML Local Runtime",
      "license": "https://dopove.com/licensing",
      "author": {
        "@type": "Organization",
        "name": "Dopove"
      }
    },
    {
      "@type": "ItemList",
      "name": "Supported Models",
      "description": "Models supported by GoingMerry local runtime",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "item": { "@type": "SoftwareApplication", "name": "Llama 4" } },
        { "@type": "ListItem", "position": 2, "item": { "@type": "SoftwareApplication", "name": "Gemma 4" } },
        { "@type": "ListItem", "position": 3, "item": { "@type": "SoftwareApplication", "name": "DeepSeek-V3" } },
        { "@type": "ListItem", "position": 4, "item": { "@type": "SoftwareApplication", "name": "Phi 4" } },
        { "@type": "ListItem", "position": 5, "item": { "@type": "SoftwareApplication", "name": "Qwen 3.0" } },
        { "@type": "ListItem", "position": 6, "item": { "@type": "SoftwareApplication", "name": "Mistral Large 3" } },
        { "@type": "ListItem", "position": 7, "item": { "@type": "SoftwareApplication", "name": "Hermes Agent" } },
        { "@type": "ListItem", "position": 8, "item": { "@type": "SoftwareApplication", "name": "Command R7" } },
        { "@type": "ListItem", "position": 9, "item": { "@type": "SoftwareApplication", "name": "LLaVA 2" } }
      ]
    }
  ]
};

// SECTION 2: 3D Projected Tensor Canvas Background
const TensorCanvas = () => {
  const canvasRef = useRef(null);
  const prefersReduced = useReducedMotion();
  const { subscribe } = useAnimationCoordinator();

  useEffect(() => {
    if (prefersReduced) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    let width = (canvas.width = canvas.offsetWidth);
    let height = (canvas.height = canvas.offsetHeight);

    const handleResize = () => {
      if (!canvas) return;
      width = canvas.width = canvas.offsetWidth;
      height = canvas.height = canvas.offsetHeight;
    };
    window.addEventListener("resize", handleResize);

    // 3D Projected Nodes - pre-compute static positions
    const nodeCount = 45;
    const nodes = [];
    for (let i = 0; i < nodeCount; i++) {
      nodes.push({
        x: (Math.random() - 0.5) * 450,
        y: (Math.random() - 0.5) * 450,
        z: (Math.random() - 0.5) * 450,
        baseX: (Math.random() - 0.5) * 450,
        baseY: (Math.random() - 0.5) * 450,
        baseZ: (Math.random() - 0.5) * 450,
      });
    }

    const connectionDist = 190;
    const pulses = [];

    const spawnPulse = () => {
      if (pulses.length > 6) return;
      const startNodeIdx = Math.floor(Math.random() * nodeCount);
      pulses.push({
        currentNode: startNodeIdx,
        progress: 0,
        path: [startNodeIdx],
      });
    };

    let pulseTimer = 0;

    // Render function using shared time from coordinator
    const renderPulses = (time) => {
      ctx.clearRect(0, 0, width, height);

      // Draw static connections and nodes (rotated by CSS transform)
      const fov = 450;
      const projected = nodes.map((node) => {
        const scale = fov / (fov + node.z);
        const projX = node.x * scale + width / 2;
        const projY = node.y * scale + height / 2;
        return { x: projX, y: projY, z: node.z, scale };
      });

      // Draw connections with alpha scaling based on depth and distance
      for (let i = 0; i < projected.length; i++) {
        for (let j = i + 1; j < projected.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dz = nodes[i].z - nodes[j].z;
          const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
          if (dist < connectionDist) {
            const alpha = (1 - dist / connectionDist) * 0.15;
            ctx.strokeStyle = `rgba(98, 183, 255, ${alpha})`;
            ctx.lineWidth = 1.0;
            ctx.beginPath();
            ctx.moveTo(projected[i].x, projected[i].y);
            ctx.lineTo(projected[j].x, projected[j].y);
            ctx.stroke();
          }
        }
      }

      // Handle pulses timer
      pulseTimer++;
      if (pulseTimer > 90) {
        spawnPulse();
        pulseTimer = 0;
      }

      // Render pulses
      pulses.forEach((pulse, pIdx) => {
        pulse.progress += 0.025;
        if (pulse.progress >= 1.0) {
          pulse.progress = 0;
          // Find neighbors
          const neighbors = [];
          for (let i = 0; i < nodes.length; i++) {
            if (i === pulse.currentNode) continue;
            const dx = nodes[pulse.currentNode].x - nodes[i].x;
            const dy = nodes[pulse.currentNode].y - nodes[i].y;
            const dz = nodes[pulse.currentNode].z - nodes[i].z;
            const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
            if (dist < connectionDist) {
              neighbors.push(i);
            }
          }

          if (neighbors.length > 0) {
            const next = neighbors[Math.floor(Math.random() * neighbors.length)];
            pulse.currentNode = next;
            pulse.path.push(next);
            if (pulse.path.length > 5) {
              pulses.splice(pIdx, 1);
            }
          } else {
            pulses.splice(pIdx, 1);
          }
        }

        if (pulse && pulse.path.length > 1) {
          const startNode = projected[pulse.path[pulse.path.length - 2]];
          const endNode = projected[pulse.currentNode];
          if (startNode && endNode) {
            const px = startNode.x + (endNode.x - startNode.x) * pulse.progress;
            const py = startNode.y + (endNode.y - startNode.y) * pulse.progress;

            ctx.fillStyle = "#62B7FF";
            ctx.shadowColor = "#62B7FF";
            ctx.shadowBlur = 12;
            ctx.beginPath();
            ctx.arc(px, py, 2.5, 0, Math.PI * 2);
            ctx.fill();

            // Glow overlay
            ctx.shadowBlur = 0;
            ctx.fillStyle = "rgba(98, 183, 255, 0.4)";
            ctx.beginPath();
            ctx.arc(px, py, 5, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      });

      // Render nodes
      projected.forEach((p) => {
        if (p.x >= 0 && p.x <= width && p.y >= 0 && p.y <= height) {
          ctx.fillStyle = `rgba(216, 221, 230, ${p.scale * 0.5})`;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.scale * 3, 0, Math.PI * 2);
          ctx.fill();
        }
      });
    };

    // Subscribe to shared animation coordinator
    const unsubscribe = subscribe('tensor-canvas', renderPulses);

    // Initial render
    renderPulses(performance.now() / 1000);

    return () => {
      window.removeEventListener("resize", handleResize);
      unsubscribe();
    };
  }, [prefersReduced, subscribe]);

  return (
    <div
      className={`absolute inset-0 w-full h-full pointer-events-none opacity-40 z-0 ${prefersReduced ? 'reduce-motion' : ''}`}
    >
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none"
      />
    </div>
  );
};
// SECTION 3: Staggered Core Trinity Feature Cards
const TrinityCard = ({ icon: Icon, title, description, delay }) => {
  const prefersReduced = useReducedMotion();

  // Apple Design: Critically damped spring (damping: 1.0, response: 0.4s)
  // Maps to Framer Motion: type: 'spring', bounce: 0, duration: 0.4
  const transition = prefersReduced
    ? { opacity: { duration: 0.2 } }
    : {
        type: 'spring',
        bounce: 0,           // Critically damped - no overshoot
        duration: 0.4,       // Apple's response: 0.4s
        delay: delay / 1000  // delay in seconds
      };

  return (
    <motion.div
      initial={prefersReduced ? { opacity: 0 } : { opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={transition}
      className="relative p-[1px] rounded-[2.5rem] overflow-hidden bg-white/5 hover-lift focus-visible-ring group"
    >
      {/* Scanning Border Sweep effect using Framer Motion instead of CSS animation */}
      {!prefersReduced && (
        <motion.div
          className="absolute inset-[-100%] pointer-events-none"
          style={{
            background: "conic-gradient(from 0deg, transparent 65%, #62B7FF 95%, #8FD4FF 100%)",
          }}
          initial={{ opacity: 0, rotate: 0 }}
          animate={{ opacity: 1, rotate: 360 }}
          transition={{ duration: 2.2, ease: "linear", repeat: Infinity }}
        />
      )}

      {/* Inside Content container */}
      <div className="relative bg-[#101A2B] p-10 rounded-[2.45rem] h-full flex flex-col items-center text-center backdrop-blur-sm z-10">
        <div className="bg-[#080B14] p-5 rounded-2xl mb-8 border border-white/5 relative">
          <Icon className="h-8 w-8 text-[#62B7FF]" />
        </div>
        <h3 className="text-2xl font-bold mb-4 text-[#D8DDE6] tracking-tight">{title}</h3>
        <p className="text-[#AAB4C4] leading-relaxed font-light text-sm">{description}</p>
      </div>
    </motion.div>
  );
};

// SECTION 4: Benchmark Matrix (Interactive terminal charts) - imported from components/story

// SECTION 5: Under the Hood Particle Architecture Stack
// Sheep peeking component
const CutePeekingSheep = () => {
  const [hovered, setHovered] = useState(false);
  const prefersReduced = useReducedMotion();

  return (
    <div
      className="fixed bottom-0 left-0 z-50 pointer-events-auto cursor-pointer hover-scale-press focus-visible-ring"
      style={{ width: '30vh', height: '60vh' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        className="relative w-full h-full transition-transform duration-300 ease-[var(--ease-out)]"
        style={{
          transform: prefersReduced ? 'translate(-7vh, 7vh)' : hovered ? 'translate(-7vh, 7vh) scale(1.02) translateY(-2vh) rotate(2deg)' : 'translate(-7vh, 7vh)'
        }}
      >
        <div
          className="absolute inset-[15%] bg-[#62B7FF]/20 rounded-full blur-3xl pointer-events-none transition-opacity duration-300 ease-[var(--ease-out)]"
          style={{ opacity: hovered && !prefersReduced ? 1 : 0 }}
        />
        <img
          src="/GoingMerry/sheep_peeking.png"
          alt="Peeking GoingMerry Sheep"
          className="w-full h-full object-contain filter drop-shadow-[0_8px_32px_rgba(98,183,255,0.35)]"
        />
        <div
          className="absolute top-[8%] left-[70%] bg-[#101A2B] border border-[#62B7FF]/30 px-4 py-2 rounded-2xl text-[11px] font-mono text-[#62B7FF] shadow-xl uppercase tracking-wider whitespace-nowrap pointer-events-none transition-opacity duration-250 ease-[var(--ease-out)]"
          style={{ opacity: hovered && !prefersReduced ? 1 : 0 }}
        >
          Merry! ���
        </div>
      </div>
    </div>
  );
};

export default function GoingMerryPage() {
  return (
    <div className="bg-[#080B14] min-h-screen text-[#D8DDE6] flex flex-col font-sans selection:bg-[#62B7FF]/30 overflow-x-hidden relative">
      <Helmet>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
        <title>GoingMerry: a local AI runtime | Dopove</title>
        <meta name="description" content="GoingMerry is a local AI runtime for running models privately on your own hardware. It is the next product we are building after ICE." />
        <meta property="og:title" content="GoingMerry: a local AI runtime | Dopove" />
        <meta property="og:description" content="GoingMerry is a local AI runtime for running models privately on your own hardware. It is the next product we are building after ICE." />
        <meta property="og:image" content="https://dopove.com/logo_new_16x9_trans_inverted.png" />
        <meta property="og:url" content="https://dopove.com/goingmerry" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="GoingMerry: a local AI runtime | Dopove" />
        <meta name="twitter:description" content="GoingMerry is a local AI runtime for running models privately on your own hardware. It is the next product we are building after ICE." />
        <meta name="twitter:image" content="https://dopove.com/logo_new_16x9_trans_inverted.png" />
        <link rel="canonical" href="https://dopove.com/goingmerry" />
      </Helmet>

      
      <main className="flex-grow">
        {/* SECTION 2: Hero Section (The Arrival) */}
        <ScrollReveal beatId="goingmerry-arrival" threshold={0.1} rootMargin="0px" className="relative min-h-screen flex flex-col items-center justify-center px-6 pt-32 pb-24 text-center overflow-hidden" data-beat-id="goingmerry-arrival">
          <TensorCanvas />

          <div className="relative z-10 w-full max-w-5xl mx-auto text-left mb-4">
            <Breadcrumb items={[{ label: 'Home', href: '/' }, { label: 'GoingMerry' }]} />
          </div>

          <div className="relative z-10 max-w-5xl mx-auto flex flex-col items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-8"
            >
              <span className="text-xs font-mono tracking-[0.3em] text-[#62B7FF] uppercase">The next product after ICE</span>
            </motion.div>

            <TypewriterHeadline
              beatId="goingmerry-claim"
              text="Run AI models on your own hardware."
              speed={50}
              delay={300}
              as="h1"
              className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black mb-8 tracking-tighter leading-[0.95] text-[#D8DDE6] min-h-[2.5em] lg:min-h-[2em]"
              cursorChar="█"
            />

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.5 }}
              className="text-lg sm:text-xl md:text-2xl text-[#AAB4C4] font-light leading-relaxed max-w-3xl mb-12"
            >
              GoingMerry is the next product we are building after ICE.
            </motion.p>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.7 }}
              className="flex flex-col sm:flex-row gap-6 mb-10 w-full justify-center px-4"
            >
              <a
                href="/download"
                className="px-12 py-5 rounded-full bg-[#62B7FF] text-[#080B14] font-bold hover-scale-press focus-visible-ring transition-[background-color,transform,box-shadow] duration-200 ease-[var(--ease-out)] hover:bg-[#8FD4FF] hover:shadow-[0_0_30px_rgba(98,183,255,0.4)] shadow-[0_0_20px_rgba(98,183,255,0.2)] tracking-wider text-xs uppercase flex items-center justify-center gap-2.5"
              >
                <Download className="w-4 h-4 shrink-0" />
                <span>Install for macOS / Windows / Linux (Coming Soon)</span>
              </a>
              <a
                href="https://docs.dopove.com"
                target="_blank"
                rel="noopener noreferrer"
                className="px-12 py-5 rounded-full border border-white/20 text-[#D8DDE6] font-bold hover-scale-press focus-visible-ring transition-[background-color,border-color,transform] duration-200 ease-[var(--ease-out)] hover:bg-white/5 hover:border-white/40 tracking-wider text-xs uppercase flex items-center justify-center gap-2.5"
              >
                <span>View Quickstart</span>
                <ChevronRight className="w-4 h-4 shrink-0 text-[#62B7FF]" />
              </a>
            </motion.div>
          </div>

          <div className="absolute bottom-10 opacity-30 pointer-events-none animate-chevron-bounce">
            <ChevronDown className="h-8 w-8 text-[#D8DDE6]" />
          </div>
        </ScrollReveal>

        {/* SECTION 3: The Core Trinity (Features Section) */}
        <ScrollReveal beatId="goingmerry-trinity" threshold={0.15} rootMargin="0px" className="bg-[#191C24] py-32 border-t border-white/5 relative" data-beat-id="goingmerry-trinity">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-24">
              <span className="text-xs font-mono tracking-[0.4em] text-[#62B7FF] uppercase mb-4 block">Capabilities</span>
              <h2 className="text-4xl md:text-5xl font-black text-white tracking-tight uppercase">The Core Trinity</h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <TrinityCard
                icon={Cpu}
                title="Native kernels"
                description="Custom CUDA, Metal and AVX kernels for local inference."
                delay={0.1}
              />
              <TrinityCard
                icon={Lock}
                title="Local by default"
                description="Runs on your own hardware, including air-gapped and on-prem deployments."
                delay={0.3}
              />
              <TrinityCard
                icon={Layers}
                title="Several hardware targets"
                description="Kernels for NVIDIA GPUs, Apple Silicon and x86 CPUs."
                delay={0.5}
              />
            </div>
          </div>
        </ScrollReveal>

        {/* SECTION 4: The Benchmark Matrix (Data Visualization) */}
        <ScrollReveal beatId="goingmerry-evidence" threshold={0.15} rootMargin="0px" className="bg-[#080B14] py-32 border-t border-white/5" data-beat-id="goingmerry-evidence">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-20">
              <h2 className="text-4xl md:text-5xl font-black text-white tracking-tight uppercase">Benchmark Metrics</h2>
            </div>

            <BenchmarkBarMatrix
              rows={[
                { label: 'Legacy Inference', value: 16.79, max: 30.5, unit: ' tok/s', color: 'ramsey' },
                { label: 'GoingMerry Engine', value: 30.50, max: 30.5, unit: ' tok/s', color: 'goingmerry', highlight: true },
              ]}
              beatId="goingmerry-evidence"
            />
            <p className="text-center text-sm text-[#AAB4C4]/70 mt-8">Internal measurement. Methodology not yet published.</p>
          </div>
        </ScrollReveal>

        {/* SECTION 5: Under the Hood (Architecture Teaser) */}
        <ScrollReveal beatId="goingmerry-under-hood" threshold={0.15} rootMargin="0px" className="bg-[#191C24] py-32 border-t border-white/5" data-beat-id="goingmerry-under-hood">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid lg:grid-cols-12 gap-16 items-center">
              {/* Left Column: Title & Graphic */}
              <div className="lg:col-span-5 space-y-8">
                <span className="text-xs font-mono tracking-[0.4em] text-[#62B7FF] uppercase block">Architecture</span>
                <h2 className="text-4xl md:text-5xl font-black text-white tracking-tight uppercase leading-none">
                  Under the Hood.
                </h2>
                <p className="text-[#AAB4C4] text-lg font-light leading-relaxed max-w-md">
                  GoingMerry pairs a Go orchestration layer with a C++ tensor inference engine, with custom kernels for each hardware target.
                </p>
                <div className="pt-4">
                  <ArchitectureStack />
                </div>
              </div>

              {/* Right Column: Spaced, Minimalist Principles */}
              <div className="lg:col-span-7 space-y-12 pl-0 lg:pl-8">
                {/* 1 */}
                <div className="space-y-2">
                  <span className="text-xs font-mono text-[#62B7FF] tracking-wider block">01 / COMPILER OPTIMIZATIONS</span>
                  <h3 className="text-xl font-bold text-white tracking-tight">Hardware-Aware Toolchain</h3>
                  <p className="text-[#AAB4C4] text-sm font-light leading-relaxed">
                    Custom C/C++ compiler toolchain implementing branchless hot-path optimization, AVX-512/NEON vectorization on CPUs, and tensor core instructions on GPUs.
                  </p>
                </div>

                {/* 2 */}
                <div className="space-y-2">
                  <span className="text-xs font-mono text-[#62B7FF] tracking-wider block">02 / CACHE ALIGNMENT</span>
                  <h3 className="text-xl font-bold text-white tracking-tight">Mastering Memory Latency</h3>
                  <p className="text-[#AAB4C4] text-sm font-light leading-relaxed">
                    Cache-aware runtime that organizes model weights to align with CPU pre-fetching mechanisms, establishing high-bandwidth L3 cache tunnels.
                  </p>
                </div>

                {/* 3 */}
                <div className="space-y-2">
                  <span className="text-xs font-mono text-[#62B7FF] tracking-wider block">03 / REAL-TIME METRICS</span>
                  <h3 className="text-xl font-bold text-white tracking-tight">Predictive Performance</h3>
                  <p className="text-[#AAB4C4] text-sm font-light leading-relaxed">
                    An empirical forecasting model that predicts local tokens-per-second output based on target memory bandwidth and model parameter sizes.
                  </p>
                </div>

                {/* 4 */}
                <div className="space-y-2">
                  <span className="text-xs font-mono text-[#62B7FF] tracking-wider block">04 / CORE STACK</span>
                  <h3 className="text-xl font-bold text-white tracking-tight">Go + C++ Hybrid Engine</h3>
                  <p className="text-[#AAB4C4] text-sm font-light leading-relaxed">
                    A polished, high-level Go orchestration layer for API and network logic driving a low-level C++ tensor inference engine compiled for speed.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </ScrollReveal>

        {/* SECTION 6: Ecosystem Integration (Infinite Marquee) */}
        <ScrollReveal beatId="goingmerry-ecosystem" threshold={0.1} className="bg-[#080B14] py-20 border-t border-white/5" data-beat-id="goingmerry-ecosystem">
          <div className="max-w-6xl mx-auto px-6 text-center mb-12">
            <span className="text-xs font-mono tracking-[0.4em] text-[#62B7FF] uppercase block">Local Ecosystem & Models</span>
          </div>
          <InfiniteMarquee colorScheme="goingmerry" beatId="goingmerry-ecosystem" height={60} />
        </ScrollReveal>

        {/* Pricing Section */}
        <section className="bg-[#191C24] py-32 border-t border-white/5 relative">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-24">
              <span className="text-xs font-mono tracking-[0.4em] text-[#62B7FF] uppercase mb-4 block">Pricing</span>
              <h2 className="text-4xl md:text-5xl font-black text-white tracking-tight uppercase">Simple Licensing</h2>
              <p className="text-[#AAB4C4] text-lg font-light leading-relaxed max-w-3xl mx-auto mt-4">
                Start free. Scale when you need to.
              </p>
            </div>

            {/* Pricing Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-5xl mx-auto">
              {/* Community Edition */}
              <div className="p-8 rounded-[2rem] bg-[#101A2B]/40 border border-white/10 flex flex-col relative hover-lift focus-visible-ring transition-[transform,border-color,background-color,box-shadow] duration-200 ease-[var(--ease-out)]">
                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-white mb-2">Community</h3>
                  <p className="text-[#AAB4C4]">Free for individuals. Run models locally.</p>
                </div>
                <div className="mb-8 pb-8 border-b border-white/10">
                  <span className="text-5xl font-bold text-white">Free</span>
                </div>
                <ul className="space-y-4 mb-8 flex-grow">
                  <li className="flex items-start text-[#D8DDE6]/90"><CheckCircle2 className="w-5 h-5 text-[#62B7FF] mr-3 shrink-0" />Free local execution</li>
                  <li className="flex items-start text-[#D8DDE6]/90"><CheckCircle2 className="w-5 h-5 text-[#62B7FF] mr-3 shrink-0" />Native CPU and GPU kernels</li>
                  <li className="flex items-start text-[#D8DDE6]/90"><CheckCircle2 className="w-5 h-5 text-[#62B7FF] mr-3 shrink-0" />Air-gapped privacy</li>
                </ul>
                <a href="/profile" className="w-full py-4 mt-auto rounded-xl bg-[#101A2B] border border-white/10 text-white font-bold text-center hover-scale-press focus-visible-ring transition-[background-color,border-color,transform,box-shadow] duration-200 ease-[var(--ease-out)] hover:bg-[#62B7FF] hover:border-[#62B7FF] hover:text-[#080B14] hover:shadow-lg block uppercase tracking-wider text-xs">
                  Join the waitlist
                </a>
              </div>

              {/* Enterprise Edition */}
              <div className="p-8 rounded-[2rem] bg-[#101A2B]/40 border border-white/10 flex flex-col relative hover-lift focus-visible-ring transition-[transform,border-color,background-color,box-shadow] duration-200 ease-[var(--ease-out)]">
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#62B7FF] text-[#080B14] px-4 py-1 rounded-full text-xs font-bold tracking-wider uppercase">
                  Enterprise
                </div>
                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-white mb-2">Enterprise</h3>
                  <p className="text-[#AAB4C4]">For teams needing compliance, support, and scale.</p>
                </div>
                <div className="mb-8 pb-8 border-b border-white/10">
                  <span className="text-4xl font-bold text-white">Custom</span>
                </div>
                <ul className="space-y-4 mb-8 flex-grow">
                  <li className="flex items-start text-[#D8DDE6]/90"><CheckCircle2 className="w-5 h-5 text-[#62B7FF] mr-3 shrink-0" />SSO, RBAC & audit logs</li>
                  <li className="flex items-start text-[#D8DDE6]/90"><CheckCircle2 className="w-5 h-5 text-[#62B7FF] mr-3 shrink-0" />Fleet management & Kubernetes</li>
                  <li className="flex items-start text-[#D8DDE6]/90"><CheckCircle2 className="w-5 h-5 text-[#62B7FF] mr-3 shrink-0" />Air-gapped deployment</li>
                </ul>
                <a href="mailto:info@dopove.com" className="w-full py-4 mt-auto rounded-xl bg-[#62B7FF] border border-[#62B7FF] text-[#080B14] font-bold text-center hover-scale-press focus-visible-ring transition-[background-color,border-color,transform,box-shadow] duration-200 ease-[var(--ease-out)] hover:bg-[#8FD4FF] hover:border-[#8FD4FF] hover:shadow-lg block uppercase tracking-wider text-xs">
                  Contact Sales
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 7: The Quickstart (Final CTA) */}
        <ScrollReveal beatId="goingmerry-quickstart" threshold={0.1} className="bg-[#080B14] py-32 border-t border-white/5" data-beat-id="goingmerry-quickstart">
          <div className="max-w-4xl mx-auto px-6 text-center space-y-12">
            <h2 className="text-4xl md:text-6xl font-black text-white tracking-tight uppercase">
              Up and running soon.
            </h2>
            <p className="text-[#AAB4C4] text-lg sm:text-xl font-light leading-relaxed max-w-2xl mx-auto">
              We are calibrating local compiler targets and model sharding frameworks. Join the waitlist to get notified the second GoingMerry becomes available for local installation.
            </p>
            <div className="flex justify-center">
              <a
                href="/profile"
                className="px-16 py-6 rounded-full bg-gradient-to-r from-[#62B7FF] to-[#8FD4FF] text-slate-950 font-black hover-scale-press focus-visible-ring transition-[transform,box-shadow] duration-200 ease-[var(--ease-out)] shadow-lg shadow-[#62B7FF]/10 hover:shadow-[#62B7FF]/20 uppercase tracking-widest text-xs"
              >
                Join Waitlist
              </a>
            </div>
          </div>
        </ScrollReveal>
      </main>

      <CutePeekingSheep />
      <Footer />
    </div>
  );
}
